<template>
  <!-- app-shared tab bar: ghost tabs with an accent underline on the active
       item. FI-1983 introduced this to replace per-surface tab dialects
       (b-tabs in erm settings, .help-tab / .dsd-tab elsewhere) — reach for
       this component instead of adding a new one. it deliberately uses its
       own class names so navbar.vue's unscoped global .nav-tabs rules can't
       bleed in (the reason erm settings needed an !important wall before). -->
  <div class="fi-tabbar" role="tablist" :aria-label="ariaLabel">
    <button
      v-for="t in tabs"
      :key="t.id"
      :id="idPrefix + '-' + t.id"
      type="button"
      role="tab"
      class="fi-tabbar-tab"
      :class="{ 'fi-tabbar-tab--active': t.id === value }"
      :aria-selected="t.id === value ? 'true' : 'false'"
      :tabindex="t.id === value ? 0 : -1"
      @click="select(t.id)"
      @keydown="onKeydown($event, t.id)"
    >{{ t.label }}<span v-if="t.dot" class="fi-tabbar-dot" aria-hidden="true"></span></button>
  </div>
</template>

<script>
export default {
  name: 'fi_tab_bar',
  props: {
    // selected tab id (v-model)
    value: { type: String, default: '' },
    // [{ id, label, dot }] — dot renders the unsaved-changes marker; pair
    // it with visible text near the panel (e.g. an "Unsaved changes" hint)
    // so screen readers get the same signal.
    tabs: { type: Array, default: () => [] },
    ariaLabel: { type: String, default: 'Tabs' },
    // tab buttons get ids `<idPrefix>-<tab.id>` so consumers can point a
    // panel's aria-labelledby at its tab.
    idPrefix: { type: String, default: 'fi-tab' },
  },
  methods: {
    select(id) {
      if (id !== this.value) this.$emit('input', id)
    },
    // roving tabindex: arrows move selection AND focus (selection follows
    // focus, the standard tablist pattern); home/end jump to the edges.
    onKeydown(evt, id) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(evt.key)) return
      evt.preventDefault()
      const ids = this.tabs.map(t => t.id)
      let i = ids.indexOf(id)
      if (evt.key === 'ArrowLeft') i = (i - 1 + ids.length) % ids.length
      else if (evt.key === 'ArrowRight') i = (i + 1) % ids.length
      else if (evt.key === 'Home') i = 0
      else i = ids.length - 1
      const next = ids[i]
      this.select(next)
      this.$nextTick(() => {
        const el = document.getElementById(this.idPrefix + '-' + next)
        if (el) el.focus()
      })
    },
  },
}
</script>

<style scoped>
/* accent is themable per consumer via --fi-tabbar-accent; falls back to
   the rail accent token, then steelblue. no wrap on narrow screens — the
   bar scrolls horizontally instead (help-tabs precedent). */
.fi-tabbar {
  display: flex;
  border-bottom: 1px solid var(--fi-line, #e1e8f0);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.fi-tabbar::-webkit-scrollbar { display: none; }
/* a mouse can't thumb-swipe: on fine-pointer devices the bar wraps onto
   extra rows instead of scrolling sideways — the hidden scrollbar leaves a
   2-d wheel pan (magic mouse, free-spin wheels) stranded mid-scroll with no
   visible way back. no viewport gate: overflow here is driven by container
   width (e.g. a narrow modal), not screen size. wrapped rows keep each
   tab's own underline; touch devices keep the one-row swipe bar. */
@media (hover: hover) and (pointer: fine) {
  .fi-tabbar {
    flex-wrap: wrap;
    overflow-x: visible;
  }
}
.fi-tabbar-tab {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--fi-text-muted, #6b7280);
  font-weight: 600;
  font-size: 13.5px;
  padding: 10px 14px;
  margin-bottom: -1px;
  cursor: pointer;
  white-space: nowrap;
  transition: color .12s, border-color .12s;
}
.fi-tabbar-tab:hover {
  color: var(--fi-tabbar-ink-active, var(--fi-ops-accent-strong, #2f5d80));
  border-bottom-color: var(--fi-line-strong, #c8d4df);
}
.fi-tabbar-tab--active {
  color: var(--fi-tabbar-ink-active, var(--fi-ops-accent-strong, #2f5d80));
  border-bottom-color: var(--fi-tabbar-accent, var(--fi-rail-accent, #4682b4));
}
/* no native ring on any focus path (the focus outline on plain :focus is
   why driver_manager_dashboard abandoned b-tabs, and safari extends focus-visible
   to clicked/programmatic focus, which drew a boxy ring around the active
   tab). keyboard users get a soft wash instead — selection follows focus
   anyway, so the moving underline is the primary cue. */
.fi-tabbar-tab:focus { outline: none; }
.fi-tabbar-tab:focus-visible {
  outline: none;
  background: rgba(70, 130, 180, 0.12);
  border-radius: 6px 6px 0 0;
}
/* unsaved-changes marker on a tab label */
.fi-tabbar-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--fi-tabbar-accent, var(--fi-ops-accent, #4682b4));
  margin-left: 6px;
  vertical-align: middle;
  position: relative;
  top: -1px;
}
</style>
